/**
 * ตัวเลือกช่วงวันที่ตามรอบเงินเดือนของหน้า "สรุปมาสาย".
 * ช่วงของรอบมาจาก cycleRange/cyclePeriodOf ตัวเดียวกับสรุปเวลาและรันเงินเดือน (@payroll/shared)
 * ไฟล์นี้ทำแค่ป้ายชื่อและรายการรอบ ไม่คิดวันตัดรอบเอง
 */
import { cycleRange, cyclePeriodOf } from '@payroll/shared';

export interface DateRange {
  from: string;
  to: string;
}

export interface CycleOption extends DateRange {
  /** งวด YYYY-MM เช่น 2026-11 = 26 ต.ค. – 25 พ.ย. */
  period: string;
  /** เช่น "พ.ย. 2569" */
  monthLabel: string;
  /** เช่น "26 ต.ค. – 25 พ.ย." */
  rangeLabel: string;
  /** เช่น "พ.ย. 2569 (26 ต.ค. – 25 พ.ย.)" */
  label: string;
}

/** วันนี้ตามเวลาไทย ไม่ขึ้นกับเขตเวลาของเครื่อง */
export function bkkToday(nowMs: number = Date.now()): string {
  return new Date(nowMs + 7 * 3_600_000).toISOString().slice(0, 10);
}

const dayMonth = new Intl.DateTimeFormat('th-TH', { day: 'numeric', month: 'short', timeZone: 'UTC' });
const monthYear = new Intl.DateTimeFormat('th-TH', { month: 'short', year: 'numeric', timeZone: 'UTC' });

export function cycleOption(period: string): CycleOption {
  const { start, end } = cycleRange(period);
  const monthLabel = monthYear.format(new Date(`${period}-15T00:00:00Z`));
  const rangeLabel = `${dayMonth.format(new Date(`${start}T00:00:00Z`))} – ${dayMonth.format(new Date(`${end}T00:00:00Z`))}`;
  return { period, from: start, to: end, monthLabel, rangeLabel, label: `${monthLabel} (${rangeLabel})` };
}

/** รอบที่วันนี้อยู่เป็นตัวแรก แล้วย้อนหลังทีละรอบ */
export function recentCycles(today: string, count: number): CycleOption[] {
  const out: CycleOption[] = [];
  let date = today;
  for (let i = 0; i < count; i++) {
    const option = cycleOption(cyclePeriodOf(date));
    out.push(option);
    // วันก่อนต้นรอบ = วันที่ 25 ของรอบก่อนหน้า
    date = new Date(Date.parse(`${option.from}T00:00:00Z`) - 86_400_000).toISOString().slice(0, 10);
  }
  return out;
}

/** ช่วงจาก URL ?from&to — รูปแบบผิดคืน null ให้หน้าใช้รอบปัจจุบันแทน (ลิงก์เสียต้องไม่ทำหน้าล้ม) */
export function rangeFromParams(from: string | null, to: string | null): DateRange | null {
  const iso = /^\d{4}-\d{2}-\d{2}$/;
  return from && to && iso.test(from) && iso.test(to) ? { from, to } : null;
}

export function sameRange(a: DateRange, b: DateRange): boolean {
  return a.from === b.from && a.to === b.to;
}

/** ช่วงที่เลือกตรงกับรอบเงินเดือนเต็มรอบหรือไม่ (null = ช่วงกำหนดเอง) */
export function cycleOfRange(range: DateRange): CycleOption | null {
  const option = cycleOption(cyclePeriodOf(range.to));
  return sameRange(option, range) ? option : null;
}

/** ปุ่ม "สรุป (CSV)" — ใช้ช่วงที่เลือกตรง ๆ */
export function summaryCsvPath(range: DateRange, includeZero: boolean): string {
  return `/hr/late-report.csv?from=${range.from}&to=${range.to}${includeZero ? '&include_zero=1' : ''}`;
}

/**
 * งวดของปุ่ม "หักมาสายรายวัน" — ยอดหักคิดเป็นรายงวด (ฟรี 3 ครั้งต่องวด) จึงออกทั้งงวดที่วันสุดท้ายของช่วงอยู่
 * เลือกรอบเต็มแล้วตรงกับรอบที่เลือกพอดี · ห้ามใช้ to.slice(0, 7): วันที่ 26 ขึ้นไปจะตกงวดก่อนหน้า
 */
export function dailyPeriodOf(range: DateRange): string {
  return cyclePeriodOf(range.to);
}

export function dailyCsvPath(range: DateRange): string {
  return `/hr/late-report/daily.csv?period=${dailyPeriodOf(range)}`;
}
